import type { TTemplateVariant } from '@/types/templates'
import { pixelsToMMRatio } from '@/helpers/templatePreviewHelperForCovers'
import { formatDecimalNumber } from '@/helpers/styles'

export function changePreviewPagesSizeAndRescaleAndRepositionElements(
  iframe: HTMLIFrameElement | null,
  newWMM: number,
  newHMM: number,
  oldPageWidthPx: number,
  newPageHeightHPx: number,
) {
  const iframeDocument = iframe?.contentDocument || iframe?.contentWindow?.document
  if (!iframeDocument) {
    return
  }

  //remove default page size css
  const linkEl = iframeDocument.getElementById('page-size-css')
  if (linkEl) {
    linkEl.remove()
  }

  const pages = iframeDocument.querySelectorAll<HTMLElement>('.cover-page-container, .back-page-container')
  const oldPageSize: { width: number; height: number } = { width: oldPageWidthPx, height: newPageHeightHPx }

  pages.forEach((page) => {
    //calculate and use new page size in pixels
    const newPageSize = {
      width: formatDecimalNumber(newWMM / pixelsToMMRatio),
      height: formatDecimalNumber(newHMM / pixelsToMMRatio),
    }
    page.style.setProperty('width', `${newPageSize.width}px`, 'important')
    page.style.setProperty('height', `${newPageSize.height}px`, 'important')

    //set attribute to body for resizing functions to work
    const body = iframeDocument.body
    body.setAttribute('data-page-size', Math.round(newPageSize.width) + 'x' + Math.round(newPageSize.height))

    doRescalingAndRepositioningOnPage(iframe, page, oldPageSize, newPageSize)
  })
}

function doRescalingAndRepositioningOnPage(
  iframe: HTMLIFrameElement,
  page: HTMLElement,
  oldPageSize: { width: number; height: number },
  newPageSize: { width: number; height: number },
) {
  const absoluteElements = Array.from(page.children) as HTMLElement[]
  absoluteElements.forEach((element: HTMLElement) => {
    if (element.tagName === 'IMG') {
      repositionAndRescaleImageElement(
        page,
        element,
        oldPageSize.height,
        oldPageSize.width,
        newPageSize.height,
        newPageSize.width,
      )
    } else {
      repositionAndRescaleElement(
        page,
        element,
        oldPageSize.height,
        oldPageSize.width,
        newPageSize.height,
        newPageSize.width,
      )

      rescaleElementFont(iframe.contentWindow as Window, element, oldPageSize, newPageSize)
    }
  })
}

function rescaleElementFont(
  win: Window,
  element: HTMLElement,
  oldPageSize: { width: number; height: number },
  newPageSize: { width: number; height: number },
) {
  if (!win) {
    return
  }

  const wRatio = newPageSize.width / oldPageSize.width
  const hRatio = newPageSize.height / oldPageSize.height
  const scale = Math.min(wRatio, hRatio)

  const computed = win.getComputedStyle(element)

  const oldFontSize = parseFloat(computed.fontSize)
  const oldLineHeight = parseFloat(computed.lineHeight) || oldFontSize * 1.2

  const newFontSize = formatDecimalNumber(oldFontSize * scale)
  const newLineHeight = formatDecimalNumber(oldLineHeight * scale)

  element.style.setProperty('font-size', `${newFontSize}px`, 'important')
  element.style.setProperty('line-height', `${newLineHeight}px`, 'important')
}

export function repositionAndRescaleElement(
  page: HTMLElement,
  element: HTMLElement,
  originalHeight: number,
  originalWidth: number,
  actualPageHeight: number,
  actualPageWidth: number,
) {
  const pageRect = page.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()

  const relativeLeft = elementRect.left - pageRect.left
  const relativeTop = elementRect.top - pageRect.top

  const scaleX = actualPageWidth / originalWidth
  const scaleY = actualPageHeight / originalHeight

  const newLeft = relativeLeft * scaleX
  const newTop = relativeTop * scaleY
  const newWidth = elementRect.width * scaleX
  const newHeight = elementRect.height * scaleY

  //don't set height of text elements. adjust width only, height will adjust to the inner content
  const isTextElement = element.tagName === 'P' || /^H[1-6]$/.test(element.tagName)

  element.style.position = 'absolute'
  element.style.left = `${newLeft}px`
  element.style.top = `${newTop}px`
  element.style.width = `${newWidth}px`
  if (!isTextElement) {
    element.style.height = `${newHeight}px`
  }
}

export function repositionAndRescaleImageElement(
  page: HTMLElement,
  imageElement: HTMLElement,
  originalHeight: number,
  originalWidth: number,
  actualPageHeight: number,
  actualPageWidth: number,
) {
  const pageRect = page.getBoundingClientRect()
  const elementRect = imageElement.getBoundingClientRect()

  const relativeLeft = elementRect.left - pageRect.left
  const relativeTop = elementRect.top - pageRect.top

  const oldWidth = elementRect.width
  const oldHeight = elementRect.height

  const scaleX = actualPageWidth / originalWidth
  const scaleY = actualPageHeight / originalHeight

  let newLeft = relativeLeft * scaleX
  let newTop = relativeTop * scaleY
  const newWidth = oldWidth * scaleX
  const newHeight = oldHeight * scaleY

  const oldRatio = oldHeight / oldWidth
  const expectedWidth = parseInt((newHeight / oldRatio).toString(), 10)

  let newRealHeight: number = newHeight
  let newRealWidth: number = newWidth

  if (expectedWidth > newWidth) {
    const availableSpaceH = newHeight
    newRealHeight = newWidth * oldRatio
    //center vertically in available space
    const emptySpaceWhichIsLeft = availableSpaceH - newRealHeight
    newTop = newTop + parseInt((emptySpaceWhichIsLeft / 2).toString(), 10)
  } else {
    const availableSpaceW = newWidth
    newRealWidth = expectedWidth
    //center horizontally in available space
    const emptySpaceWhichIsLeft = availableSpaceW - newRealWidth
    newLeft = newLeft + parseInt((emptySpaceWhichIsLeft / 2).toString(), 10)
  }

  imageElement.style.position = 'absolute'
  imageElement.style.left = `${newLeft}px`
  imageElement.style.top = `${newTop}px`
  imageElement.style.width = `${newRealWidth}px`
  imageElement.style.height = `${newRealHeight}px`
}

export function adjustH1FontSize(
  page: HTMLElement,
  variant: TTemplateVariant,
  actualPageSize: { width: number; height: number } | null,
  newTitleToSet?: string,
) {
  const h1: HTMLHeadingElement | undefined = Array.from(page.children).find((el) => el.tagName === 'H1') as
    | HTMLHeadingElement
    | undefined
  if (!h1 || !actualPageSize) {
    return
  }

  if (newTitleToSet) {
    h1.innerText = newTitleToSet
  }

  const h1InnerVariant = variant?.inner_variants?.find((iv) => iv.element_type === 'h1')?.inner_variant
  if (!h1InnerVariant?.html) {
    return
  }

  const parsedInnerVariant = new DOMParser().parseFromString(h1InnerVariant.html, 'text/html')
  if (parsedInnerVariant.querySelector('parsererror')) {
    return
  }

  //In case of missing data attributes, use default values
  const defaultInitialHeight = '250'
  const defaultInitialWidth = '550'
  const dataInitialHeight =
    parsedInnerVariant.querySelector('h1')?.getAttribute('data-initial-height') ?? defaultInitialHeight
  const dataInitialWidth =
    parsedInnerVariant.querySelector('h1')?.getAttribute('data-initial-width') ?? defaultInitialWidth

  const originalHeight = variant.entire_page_height
  const originalWidth = variant.entire_page_width

  const actualPageHeight = actualPageSize?.height ?? 0
  const actualPageWidth = actualPageSize?.width ?? 0

  const heightRatio = actualPageHeight / originalHeight
  const widthRatio = actualPageWidth / originalWidth

  const expectedHeight = Number((parseFloat(dataInitialHeight) * heightRatio).toFixed(2))
  let expectedWidth = Number((parseFloat(dataInitialWidth) * widthRatio).toFixed(2))
  const maxPossibleWidth = actualPageWidth - parseInt(h1.style?.left ?? 0)
  expectedWidth = Math.min(expectedWidth, maxPossibleWidth)

  h1.style.height = `${expectedHeight}px`
  h1.style.width = `${expectedWidth}px`

  //remove inline font height before auto adjusting
  h1.style.height = ''
  //increase font size first to max value possible
  increaseFontSize(h1, expectedHeight)
  //decrease font size next if needed
  decreaseFontSizeToFit(h1, expectedHeight)
}

function increaseFontSize(textContainer: HTMLElement, goalHeight: number) {
  const maxAttempts = 150
  const maxFontSize = 150

  const computedContainerStyles = window.getComputedStyle(textContainer)
  let containerFontSize = parseInt(computedContainerStyles.fontSize.replace('px', ''))
  let textContainerRect = textContainer.getBoundingClientRect()
  let containerHeight = textContainerRect.height

  for (let i = 0; i < maxAttempts; i++) {
    if (containerHeight < goalHeight) {
      if (containerFontSize + 1 <= maxFontSize) {
        containerFontSize = containerFontSize + 1
        textContainer.style.fontSize = containerFontSize + 'px'
        textContainer.style.lineHeight = Math.floor(containerFontSize * 1.1) + 'px'
        textContainerRect = textContainer.getBoundingClientRect()
        containerHeight = textContainerRect.height
      } else {
        break
      }
    } else {
      break
    }
  }
}

function decreaseFontSizeToFit(textContainer: HTMLElement, goalHeight: number) {
  const maxAttempts = 150
  const minFontSize = 10

  const computedContainerStyles = window.getComputedStyle(textContainer)
  let containerFontSize = parseInt(computedContainerStyles.fontSize.replace('px', ''))
  let textContainerRect = textContainer.getBoundingClientRect()
  let containerHeight = textContainerRect.height

  for (let i = 0; i < maxAttempts; i++) {
    if (containerHeight > goalHeight) {
      if (containerFontSize - 1 >= minFontSize) {
        containerFontSize = containerFontSize - 1
        textContainer.style.fontSize = containerFontSize + 'px'
        textContainer.style.lineHeight = Math.floor(containerFontSize * 1.1) + 'px'
        textContainerRect = textContainer.getBoundingClientRect()
        containerHeight = textContainerRect.height
      } else {
        break
      }
    } else {
      break
    }
  }
}
